import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Quote,
  Link as LinkIcon, Image as ImageIcon, Undo2, Redo2, Heading1, Heading2, Heading3,
  Save, Send, Eye, EyeOff, Upload, X, CheckCircle2,
} from 'lucide-react';
import { authUrlWithNext } from '../lib/whatsapp';
import { Button } from '../components/ui/button.jsx';
import { useAuth } from '../context/AuthContext';
import OrnateDivider from '../components/OrnateDivider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  'Tantra', 'Yoga', 'Pranayama', 'Meditation', 'Philosophy',
  'Lifestyle, Diet & Health', 'Ayurveda', 'Lineage', 'Practice', 'Consciousness',
];
const AUTOSAVE_MS = 90_000;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/* ---------- Toolbar ---------- */
const ToolBtn = ({ active, onClick, title, children, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={`p-2 rounded-lg transition ${
      active ? 'bg-primary/20 text-primary' : 'text-primary/70 hover:bg-primary/10'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    data-testid={testid}
  >
    {children}
  </button>
);

const Toolbar = ({ editor, onInsertImage }) => {
  if (!editor) return null;
  const is = (name, attrs) => editor.isActive(name, attrs);
  const run = (fn) => () => fn(editor.chain().focus()).run();

  const addLink = () => {
    const url = window.prompt('Enter URL');
    if (!url) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-primary/20 bg-background/60 sticky top-0 z-10">
      <ToolBtn active={is('heading', { level: 1 })} onClick={run((c) => c.toggleHeading({ level: 1 }))} title="Heading 1" testid="tb-h1"><Heading1 size={18} /></ToolBtn>
      <ToolBtn active={is('heading', { level: 2 })} onClick={run((c) => c.toggleHeading({ level: 2 }))} title="Heading 2" testid="tb-h2"><Heading2 size={18} /></ToolBtn>
      <ToolBtn active={is('heading', { level: 3 })} onClick={run((c) => c.toggleHeading({ level: 3 }))} title="Heading 3" testid="tb-h3"><Heading3 size={18} /></ToolBtn>
      <div className="w-px h-6 bg-primary/20 mx-1" />
      <ToolBtn active={is('bold')} onClick={run((c) => c.toggleBold())} title="Bold" testid="tb-bold"><Bold size={18} /></ToolBtn>
      <ToolBtn active={is('italic')} onClick={run((c) => c.toggleItalic())} title="Italic" testid="tb-italic"><Italic size={18} /></ToolBtn>
      <ToolBtn active={is('underline')} onClick={run((c) => c.toggleUnderline())} title="Underline" testid="tb-underline"><UnderlineIcon size={18} /></ToolBtn>
      <div className="w-px h-6 bg-primary/20 mx-1" />
      <ToolBtn active={is('bulletList')} onClick={run((c) => c.toggleBulletList())} title="Bulleted list" testid="tb-ul"><List size={18} /></ToolBtn>
      <ToolBtn active={is('orderedList')} onClick={run((c) => c.toggleOrderedList())} title="Numbered list" testid="tb-ol"><ListOrdered size={18} /></ToolBtn>
      <ToolBtn active={is('blockquote')} onClick={run((c) => c.toggleBlockquote())} title="Blockquote" testid="tb-quote"><Quote size={18} /></ToolBtn>
      <div className="w-px h-6 bg-primary/20 mx-1" />
      <ToolBtn active={is('link')} onClick={addLink} title="Insert link" testid="tb-link"><LinkIcon size={18} /></ToolBtn>
      <ToolBtn onClick={onInsertImage} title="Insert image" testid="tb-image"><ImageIcon size={18} /></ToolBtn>
      <div className="w-px h-6 bg-primary/20 mx-1" />
      <ToolBtn onClick={run((c) => c.undo())} title="Undo" testid="tb-undo"><Undo2 size={18} /></ToolBtn>
      <ToolBtn onClick={run((c) => c.redo())} title="Redo" testid="tb-redo"><Redo2 size={18} /></ToolBtn>
    </div>
  );
};

/* ---------- Image uploader helper ---------- */
const uploadImage = async (file, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${API}/blog/upload-image`, form, {
    headers: { ...authHeader() },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data;
};

/* ---------- Tag chip input ---------- */
const TagInput = ({ tags, setTags }) => {
  const [value, setValue] = useState('');
  const addTag = () => {
    const v = value.trim();
    if (!v) return;
    if (tags.includes(v)) { setValue(''); return; }
    if (tags.length >= 5) { toast.error('Max 5 tags'); return; }
    setTags([...tags, v]);
    setValue('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-primary/10 border border-primary/30 text-primary">
            {t}
            <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-primary/60">
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
        onBlur={addTag}
        placeholder="Add a tag and press Enter"
        className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
        data-testid="blog-editor-tags-input"
      />
    </div>
  );
};

/* ---------- Main editor ---------- */
const BlogEditor = () => {
  const { user, loading: authLoading } = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname.startsWith('/blog/edit/');

  const [role, setRole] = useState(null);
  const [postId, setPostId] = useState(null);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [tags, setTags] = useState([]);
  const [coverImage, setCoverImage] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [pinRequested, setPinRequested] = useState(false);
  const [innerCircleOnly, setInnerCircleOnly] = useState(false);
  const [status, setStatus] = useState('draft');
  const [feedback, setFeedback] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [savingLabel, setSavingLabel] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [readTime, setReadTime] = useState(1);
  const [authorName, setAuthorName] = useState('');
  const [imageUploadProgress, setImageUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const lastSavedPayloadRef = useRef('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'underline text-primary' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full my-4' } }),
      Placeholder.configure({ placeholder: 'Begin your writing...' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose-blog min-h-[400px] focus:outline-none px-6 py-6 text-primary/90',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const text = ed.getText();
      setReadTime(Math.max(1, Math.round(text.split(/\s+/).filter(Boolean).length / 200)));
    },
  });

  /* Load role */
  useEffect(() => {
    if (authLoading || !user) return;
    axios.get(`${API}/blog/my-role`, { headers: authHeader() })
      .then((r) => setRole(r.data.role))
      .catch(() => setRole('reader'));
    setAuthorName(user.name);
  }, [user, authLoading]);

  /* Access guard: writer or admin */
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(authUrlWithNext(location)); return; }
    if (role && role === 'reader') {
      toast.error('Writer or Admin access required');
      navigate('/blog');
    }
  }, [role, user, authLoading, navigate]);

  /* Load existing post if editing */
  useEffect(() => {
    if (!isEdit || !slug || !editor) return;
    // Send auth header so the backend can authorise admins/authors to fetch
    // their own drafts / pending posts (public 404s without it).
    axios.get(`${API}/blog/${slug}`, { headers: authHeader() })
      .then((r) => {
        const p = r.data;
        setPostId(p.id);
        setTitle(p.title);
        setSubtitle(p.subtitle || '');
        setCategory(p.category);
        setTags(p.tags || []);
        setCoverImage(p.cover_image_url || '');
        setPinRequested(!!p.is_homepage_pinned);
        setInnerCircleOnly(!!p.inner_circle_only);
        setStatus(p.status);
        setFeedback(p.rejection_feedback || '');
        setAuthorName(p.author_name);
        editor.commands.setContent(p.content || '');
        setReadTime(p.read_time || 1);
      })
      .catch(async () => {
        // Fall back: try the author's own list (covers slug-mismatch edge cases)
        try {
          const r = await axios.get(`${API}/blog/my-posts`, { headers: authHeader() });
          const found = r.data.find((p) => p.slug === slug);
          if (found) {
            setPostId(found.id);
            setTitle(found.title);
            setSubtitle(found.subtitle || '');
            setCategory(found.category);
            setTags(found.tags || []);
            setCoverImage(found.cover_image_url || '');
            setPinRequested(!!found.is_homepage_pinned);
            setInnerCircleOnly(!!found.inner_circle_only);
            setStatus(found.status);
            setFeedback(found.rejection_feedback || '');
            setAuthorName(found.author_name);
            editor.commands.setContent(found.content || '');
            setReadTime(found.read_time || 1);
          } else {
            toast.error('Post not found');
            navigate('/dashboard');
          }
        } catch {
          toast.error('Could not load post');
          navigate('/dashboard');
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, slug, editor]);

  /* Save helper */
  const buildPayload = useCallback(() => ({
    title: title.trim(),
    subtitle: subtitle.trim(),
    content: editor ? editor.getHTML() : '',
    cover_image_url: coverImage || '',
    category,
    tags,
    is_homepage_pinned: pinRequested,
    inner_circle_only: innerCircleOnly,
    author_name: (authorName || '').trim(),
  }), [title, subtitle, editor, coverImage, category, tags, pinRequested, innerCircleOnly, authorName]);

  const validate = (payload) => {
    if (!payload.title || payload.title.length < 5) {
      toast.error('Title must be at least 5 characters');
      return false;
    }
    if (payload.subtitle && payload.subtitle.length > 160) {
      toast.error('Subtitle max 160 characters');
      return false;
    }
    return true;
  };

  const saveDraft = useCallback(async (silent = false) => {
    if (!editor) return;
    const payload = buildPayload();
    const serialised = JSON.stringify(payload);
    if (serialised === lastSavedPayloadRef.current) return; // nothing changed
    if (!validate(payload)) return;
    try {
      setSavingLabel('Saving…');
      if (!postId) {
        const r = await axios.post(`${API}/blog`, payload, { headers: authHeader() });
        setPostId(r.data.id);
        setStatus(r.data.status);
        if (!silent) toast.success('Draft saved');
        // Intentionally NOT navigating here — remounting the editor on autosave
        // interrupts typing (reported issue). User can refresh later if they
        // want the edit URL.
      } else {
        const r = await axios.put(`${API}/blog/${postId}`, payload, { headers: authHeader() });
        setStatus(r.data.status);
        if (!silent) toast.success('Draft saved');
      }
      lastSavedPayloadRef.current = serialised;
      setLastSavedAt(new Date());
      setSavingLabel('Saved');
      setTimeout(() => setSavingLabel(''), 2000);
    } catch (err) {
      setSavingLabel('');
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  }, [editor, postId, buildPayload]);

  /* Auto-save every 90s */
  useEffect(() => {
    if (!editor) return;
    const iv = setInterval(() => { saveDraft(true); }, AUTOSAVE_MS);
    return () => clearInterval(iv);
  }, [editor, saveDraft]);

  const submitForReview = async () => {
    await saveDraft(true);
    if (!postId) return;
    try {
      await axios.post(`${API}/blog/${postId}/submit`, {}, { headers: authHeader() });
      toast.success('Submitted for review');
      setStatus('pending_review');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    }
  };

  const publish = async () => {
    await saveDraft(true);
    if (!postId) return;
    try {
      await axios.post(`${API}/blog/${postId}/publish`, {}, { headers: authHeader() });
      toast.success('Published');
      setStatus('published');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Publish failed');
    }
  };

  const unpublish = async () => {
    if (!postId) return;
    try {
      await axios.post(`${API}/blog/${postId}/unpublish`, {}, { headers: authHeader() });
      toast.success('Reverted to draft');
      setStatus('draft');
      setPinRequested(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unpublish failed');
    }
  };

  /* Image insertion (inline) */
  const triggerInlineImage = () => fileInputRef.current?.click();
  const handleInlineImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be ≤5MB'); return; }
    try {
      setImageUploadProgress(0);
      const res = await uploadImage(file, setImageUploadProgress);
      editor?.chain().focus().setImage({ src: res.url, alt: file.name }).run();
      toast.success('Image inserted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setImageUploadProgress(null);
    }
  };

  const handleCoverImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be ≤5MB'); return; }
    try {
      setCoverUploading(true);
      const res = await uploadImage(file);
      setCoverImage(res.url);
      toast.success('Cover image set');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setCoverUploading(false);
    }
  };

  const isAdmin = role === 'admin';
  const isWriter = role === 'writer';
  const canEditAuthor = isAdmin; // per spec

  if (authLoading || role === null) {
    return (
      <div className="min-h-screen mystical-bg flex items-center justify-center">
        <div className="text-primary/70">Loading editor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mystical-bg pt-24 pb-20" data-testid="blog-editor-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary/60">
              {isEdit ? 'Edit post' : 'New post'} · Status: <span className="text-primary/90">{status}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading golden-text tracking-wider">Writer's Studio</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {savingLabel && <span className="text-xs text-primary/60 flex items-center gap-1"><CheckCircle2 size={14} />{savingLabel}</span>}
            <Button variant="outline" onClick={() => setPreviewMode((p) => !p)} data-testid="blog-editor-preview-btn">
              {previewMode ? <><EyeOff size={16} className="mr-2" />Edit</> : <><Eye size={16} className="mr-2" />Preview</>}
            </Button>
            <Button onClick={() => saveDraft(false)} className="ornate-button" data-testid="blog-editor-save-btn">
              <Save size={16} className="mr-2" />{status === 'published' ? 'Update Post' : 'Save Draft'}
            </Button>
            {isWriter && status !== 'pending_review' && status !== 'published' && (
              <Button onClick={submitForReview} className="ornate-button" data-testid="blog-editor-submit-btn">
                <Send size={16} className="mr-2" />Submit for Review
              </Button>
            )}
            {isAdmin && status !== 'published' && (
              <Button onClick={publish} className="ornate-button" data-testid="blog-editor-publish-btn">
                Publish
              </Button>
            )}
            {isAdmin && status === 'published' && (
              <Button variant="outline" onClick={unpublish} data-testid="blog-editor-unpublish-btn">
                Unpublish
              </Button>
            )}
            {isAdmin && postId && (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
                  try {
                    await axios.delete(`${API}/blog/${postId}`, { headers: authHeader() });
                    navigate('/blog');
                  } catch (_e) { /* ignore */ }
                }}
                className="border-rose-400/40 text-rose-200 hover:bg-rose-500/10"
                data-testid="blog-editor-delete-btn"
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {feedback && status === 'rejected' && (
          <div className="mb-4 p-4 rounded-lg border border-rose-400/40 bg-rose-500/5 text-rose-200 text-sm" data-testid="blog-editor-feedback">
            <strong className="block mb-1">Admin feedback:</strong>
            {feedback}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* Editor column */}
          <div className="card-mystical rounded-2xl overflow-hidden" data-testid="blog-editor-surface">
            {!previewMode ? (
              <>
                <div className="p-6 border-b border-primary/20">
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    autoComplete="off"
                    spellCheck="true"
                    placeholder="Title"
                    className="w-full bg-transparent text-3xl md:text-4xl font-heading golden-text tracking-wide focus:outline-none"
                    data-testid="blog-editor-title"
                  />
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    autoComplete="off"
                    placeholder="Subtitle (optional, max 160 chars)"
                    maxLength={160}
                    className="w-full bg-transparent mt-2 text-base text-primary/75 italic focus:outline-none"
                    data-testid="blog-editor-subtitle"
                  />
                </div>
                <Toolbar editor={editor} onInsertImage={triggerInlineImage} />
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleInlineImage} />
                {imageUploadProgress !== null && (
                  <div className="px-6 pt-3 text-xs text-primary/60">
                    Uploading... {imageUploadProgress}%
                    <div className="h-1 bg-primary/10 rounded mt-1">
                      <div className="h-1 bg-primary rounded" style={{ width: `${imageUploadProgress}%` }} />
                    </div>
                  </div>
                )}
                <EditorContent editor={editor} />
              </>
            ) : (
              <div className="p-8">
                <div className="text-xs uppercase tracking-[0.3em] text-primary/50 mb-3">{category} · {readTime} min read</div>
                <h1 className="text-3xl md:text-4xl font-heading golden-text tracking-wider mb-2">{title || '(untitled)'}</h1>
                {subtitle && <p className="text-primary/75 italic mb-6">{subtitle}</p>}
                {coverImage && (
                  <div className="aspect-[16/9] rounded-xl overflow-hidden mb-6 border border-primary/20">
                    <img src={coverImage} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <OrnateDivider variant="diamond" className="mb-8 max-w-md mx-auto" />
                <div className="prose-blog text-primary/90" dangerouslySetInnerHTML={{ __html: editor?.getHTML() || '' }} />
              </div>
            )}
          </div>

          {/* Metadata sidebar */}
          <aside className="space-y-5">
            <div className="card-mystical rounded-2xl p-5 space-y-4">
              <h3 className="text-xs uppercase tracking-[0.3em] text-primary/60 font-heading">Post details</h3>

              <label className="block text-sm text-primary/80">
                Category
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
                  data-testid="blog-editor-category"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>

              <div>
                <div className="text-sm text-primary/80 mb-1">Tags (max 5)</div>
                <TagInput tags={tags} setTags={setTags} />
              </div>

              <label className="block text-sm text-primary/80">
                Author
                <input
                  type="text"
                  value={authorName}
                  onChange={(e) => canEditAuthor && setAuthorName(e.target.value)}
                  readOnly={!canEditAuthor}
                  className={`mt-1 w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 text-sm text-primary ${canEditAuthor ? '' : 'opacity-70'} focus:outline-none focus:border-primary/60`}
                  data-testid="blog-editor-author"
                />
              </label>

              <div className="text-sm text-primary/80">
                Read time: <span className="font-medium text-primary">{readTime} min read</span>
              </div>
            </div>

            <div className="card-mystical rounded-2xl p-5 space-y-3">
              <h3 className="text-xs uppercase tracking-[0.3em] text-primary/60 font-heading">Cover image</h3>
              {coverImage && (
                <img src={coverImage} alt="cover" className="w-full aspect-[16/9] object-cover rounded-lg border border-primary/20" />
              )}
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={coverUploading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-primary/30 rounded-lg text-primary/80 hover:bg-primary/5 transition"
                data-testid="blog-editor-cover-upload"
              >
                <Upload size={16} /> {coverUploading ? 'Uploading…' : (coverImage ? 'Replace cover' : 'Upload cover')}
              </button>
              <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleCoverImage} />
              <p className="text-[11px] text-primary/50 leading-relaxed">
                Cover image: 1200 × 630px recommended (16:9). JPG · PNG · WebP · max 5MB.
              </p>
            </div>

            <div className="card-mystical rounded-2xl p-5">
              <label className={`flex items-start gap-3 cursor-pointer ${isWriter ? 'opacity-60' : ''}`} title={isWriter ? 'Only Admin can pin posts to the homepage.' : ''}>
                <input
                  type="checkbox"
                  checked={pinRequested}
                  onChange={(e) => setPinRequested(e.target.checked)}
                  disabled={isWriter}
                  className="accent-primary mt-1"
                  data-testid="blog-editor-pin-checkbox"
                />
                <div>
                  <div className="text-sm font-medium text-primary">Pin to homepage</div>
                  <div className="text-xs text-primary/60">
                    {isWriter
                      ? 'Only Admin can pin posts to the homepage.'
                      : 'Admins can pin up to 3 published posts. Unpublishing auto-removes the pin.'}
                  </div>
                </div>
              </label>
            </div>

            <div className="card-mystical rounded-2xl p-5">
              <label className={`flex items-start gap-3 cursor-pointer ${isWriter ? 'opacity-60' : ''}`} title={isWriter ? 'Only Admin can gate posts to Inner Circle members.' : ''}>
                <input
                  type="checkbox"
                  checked={innerCircleOnly}
                  onChange={(e) => setInnerCircleOnly(e.target.checked)}
                  disabled={isWriter}
                  className="accent-primary mt-1"
                  data-testid="blog-editor-inner-circle-checkbox"
                />
                <div>
                  <div className="text-sm font-medium text-primary">Inner Circle only</div>
                  <div className="text-xs text-primary/60">
                    Non-members read the first two paragraphs, then a gate card
                    invites them to join. Preview text stays in the page source
                    for search engines.
                  </div>
                </div>
              </label>
            </div>

            <div className="card-mystical rounded-2xl p-5 text-xs text-primary/60 leading-relaxed">
              <strong className="text-primary/80">Inline image tips</strong>
              <p className="mt-1">Recommended: 1200 × 630px (16:9) — displays correctly on all devices and social shares.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
